import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LeftArrow } from '../../../../assets/icons/left-arrow.svg';
import { ReactComponent as LocationIcon } from '../../../../assets/icons/location-icon.svg';
import { ReactComponent as RightArrow } from '../../../../assets/icons/right-arrow.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import DefaultApiParameters from '../../../../enum/DefaultApiParameters';
import currentTimeZone from '../../../../enum/timeZone';
import {
  MaintenanceScheduleProperties,
  MaintenanceScheduleRequest,
} from '../../../../interface/maintenance-schedule.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchMaintenanceSchedule,
  fetchMaintenanceScheduleFilters,
  updateMaintenanceScheduleFilters,
  setSortData,
} from '../../../../redux/slices/MaintenanceScheduleSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import { changeDateFormat } from '../../../../shared-functions/DateTime';
import device from '../../../../utils/api/device';

interface MaintenanceScheduleReportProperties {
  day: number;
  month: number;
  year: number;
  status: string;
}

/**
 * Component to disable charging schedule based on charging point
 * @returns
 */
function MaintenanceSchedule() {
  const { t, i18n } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const {
    schedules,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    sortBy,
    sortField,
  } = useAppSelector((s) => s.maintenanceSchedule);
  const { selectedChargingPoint } = useAppSelector((s) => s.device);
  const [maintenanceReport, setMaintenanceReport] = useState<MaintenanceScheduleReportProperties[]>(
    [],
  );

  const months = [
    t('january'),
    t('february'),
    t('march'),
    t('april'),
    t('may'),
    t('june'),
    t('july'),
    t('august'),
    t('september'),
    t('october'),
    t('november'),
    t('december'),
  ];
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [allDates, setAllDates] = useState<any[]>([]);

  const column = [
    {
      label: t('maintenanceDate'),
      accessorKey: 'date' as keyof MaintenanceScheduleProperties,
      sortAllow: true,
    },
    {
      label: t('maintenanceTime'),
      accessorKey: 'time' as keyof MaintenanceScheduleProperties,
      sortAllow: true,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof MaintenanceScheduleProperties,
      sortAllow: true,
    },
    {
      label: t('stationLocation'),
      accessorKey: 'stationLocation' as keyof MaintenanceScheduleProperties,
      sortAllow: true,
      /**
       * maintenance
       */
      customData: (maintenance: MaintenanceScheduleProperties) => (
        <div className="location-in-maintenance-table">
          <span>
            <LocationIcon />
          </span>
          <p className="table-row-each-data">{maintenance.address}</p>
        </div>
      ),
    },
    {
      label: t('duration'),
      accessorKey: 'duration' as keyof MaintenanceScheduleProperties,
      sortAllow: true,
    },
  ];

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    dispatch(
      fetchMaintenanceSchedule({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: MaintenanceScheduleRequest) => {
    dispatch(
      fetchMaintenanceSchedule({
        ...value,
        sortBy,
        sortField,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * Get the all days use month function
   * @param month
   * @param year
   */
  const getAllDays: (month: number, year: number) => void = (month: number, year: number) => {
    const date = new Date(year, month, 1);
    let days = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    days = days.map((valueChange) => ({
      date: changeDateFormat(valueChange.toString()),
      number: valueChange.getDate(),
      day: valueChange.getDay(),
    }));
    if (days[0].day > 0) {
      const firstMonth = days[0].day;
      for (let index = 0; index < firstMonth; index += 1) {
        days = [{}, ...days];
      }
    }
    setAllDates(days);
  };

  /**
   * Handle the month change function
   * @param type
   */
  const changeMonth = (type: string) => {
    let changeValue = 0;
    let changeYear = currentYear;
    if (type === 'add') {
      changeValue = currentMonth + 1;
      if (changeValue === months.length) {
        changeValue = 0;
        changeYear = currentYear + 1;
        setCurrentYear(currentYear + 1);
      }
      getAllDays(changeValue, changeYear);

      setCurrentMonth(changeValue);
    } else {
      changeValue = currentMonth - 1;
      if (changeValue === -1) {
        changeValue = 11;
        changeYear = currentYear - 1;
        setCurrentYear(currentYear - 1);
      }
      getAllDays(changeValue, changeYear);
      setCurrentMonth(changeValue);
    }
  };

  /**
   * Function to fetch maintenance schedule report
   */
  const handleFetchMaintenanceReport = async (data: any) => {
    try {
      const response = await device.fetchMaintenanceScheduleReport(data);
      setMaintenanceReport(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Function to return a maintenance
   * @param date
   */
  const handleShowMaintenanceSchedule = (date: string): string => {
    const [year, month, day] = date.split('-');

    const numericDay = Number(day); // 1
    const numericMonth = Number(month); // 10

    const maintenance = maintenanceReport.find(
      (eachReport) =>
        eachReport?.day?.toString() === numericDay.toString() &&
        eachReport?.month?.toString() === numericMonth.toString() &&
        eachReport?.year?.toString() === year,
    );

    if (!maintenance) return 'no-schedule';

    return maintenance?.status;
  };

  useEffect((): void => {
    getAllDays(currentMonth, currentYear);
  }, []);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    if (selectedChargingPoint?.id) {
      dispatch(
        fetchMaintenanceSchedule({
          page: DefaultApiParameters.page,
          limit: DefaultApiParameters.limit,
          chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
          ...queryParameters,
        }),
      );
    }
  }, [filters, selectedChargingPoint]);

  useEffect(() => {
    if (selectedChargingPoint?.id) {
      handleFetchMaintenanceReport({
        chargingPointSn: selectedChargingPoint?.chargingPointSn,
        timeZone: currentTimeZone,
      });
    }
  }, [selectedChargingPoint]);

  return (
    <div className="device-maintenance-schedules-wrap" dir={i18n.dir()}>
      <div className="device-charging-schedules-table">
        <CustomTable
          version="v2"
          header={column}
          data={schedules}
          serialNo
          filter={{}}
          filterClearFunction={() => {}}
          filterFunction={() => {}}
          actionButton={false}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateMaintenanceScheduleFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchMaintenanceScheduleFilters}
        />
      </div>
      <div className="maintenance-chart">
        <div className="maintenance-calendar-wrap">
          <div className="calender-header">
            <div
              className="calender-header-left"
              onClick={() => changeMonth('sub')}
              role="presentation"
              style={{
                transform: i18n.dir() === 'rtl' ? 'rotate(0deg)' : 'rotate(180deg)',
              }}>
              <LeftArrow />
            </div>
            <div className="calender-header-center">
              {months[currentMonth]} - {currentYear}
            </div>
            <div
              className="calender-header-right"
              onClick={() => changeMonth('add')}
              role="presentation"
              style={{
                transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
              <RightArrow />
            </div>
          </div>
          <div className="calender-body">
            <div className="calender-header-items">
              <div className="header-item">S</div>
              <div className="header-item">M</div>
              <div className="header-item">T</div>
              <div className="header-item">W</div>
              <div className="header-item">T</div>
              <div className="header-item">F</div>
              <div className="header-item">S</div>
            </div>
            <div className="calender-body-items">
              {allDates.map((dateDetail: any) =>
                Object.keys(dateDetail).length === 0 ? (
                  <div className="header-item" />
                ) : (
                  <div
                    className="header-item"
                    // onClick={() => handleValueChange(dateDetail.date)}
                    role="presentation">
                    <p className={handleShowMaintenanceSchedule(dateDetail?.date || '')}>
                      {dateDetail.number}
                    </p>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
        {/* <div>Other chart</div> */}
      </div>
    </div>
  );
}

export default MaintenanceSchedule;
